import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { formatPrice } from 'utils/format';
import productCoverImage from 'utils/productCoverImage';

import { Badge, Card, Typography } from '../base';

import CloudinaryPicture from './cloudinaryPicture';
import PriceBadge from './priceBadge';
import SaleBadge from './saleBadge';

const ProductCard = forwardRef((props, ref) => {
  const className = classNames({
    'card-equalize': true,
    'card-product': true,
    [props.className]: props.className,
  });

  const price = formatPrice(props.product.prices[0], props.product.currency, true);
  const hasPriceRange = props.startingAt || props.product.prices.length > 1;

  // The badge names the discount on every covered product (the signal shoppers scan a grid for).
  // The price chip additionally shows the lowered "from" price when a percentage coupon lowers it.
  const discountedPrice =
    props.sale?.discountedPriceFrom != null
      ? formatPrice(props.sale.discountedPriceFrom, props.product.currency, true)
      : null;
  const saleBadge = props.sale?.badge;

  const disabled = props.product.config?.disabled === true;

  const coverImage = productCoverImage(props.product);

  return (
    <Card ref={ref} {...props} className={className}>
      <div className="card-img-top">
        <CloudinaryPicture prefix={props.imagePrefix} imageKey={coverImage?.key} widths={props.widths} sizes={props.sizes} style={disabled ? { filter: "grayscale(100%)" } : {}} />
        {disabled ? (
          <PriceBadge muted>Coming Soon</PriceBadge>
        ) : (
          <>
            {price && (
              <PriceBadge
                prefix={hasPriceRange ? 'Starting at' : null}
                originalPrice={discountedPrice ? price : undefined}
              >
                {discountedPrice ?? price}
              </PriceBadge>
            )}
            {saleBadge && <SaleBadge>{saleBadge}</SaleBadge>}
          </>
        )}
      </div>
      <Card.Body>
        <Typography.H4 className="mb-0">
          <Typography.Span>{props.product.name}</Typography.Span>
          {props.product.new && (
            <div className='d-inline position-relative' style={{ marginLeft: 4 }}>
              <Badge type="success" size="sm" className="position-absolute">NEW</Badge>
            </div>
          )}
        </Typography.H4>
      </Card.Body>
    </Card>
  );
});

ProductCard.propTypes = {
  asLink: PropTypes.bool,
  product: PropTypes.object,
  className: PropTypes.string,
  children: PropTypes.node,
  imagePrefix: PropTypes.string,
  startingAt: PropTypes.bool,
  // Forwarded to CloudinaryPicture; the caller knows the rendered layout (grid cols differ per page).
  // Only pass widths when imagePrefix points to a folder with a CDN ladder (products/, not sku/).
  widths: PropTypes.arrayOf(PropTypes.number),
  sizes: PropTypes.string,
  // Claimed-coupon sale treatment, as computed by `ecommerce/lib/coupon` couponSale().
  sale: PropTypes.shape({
    badge: PropTypes.string,
    discountedPriceFrom: PropTypes.number,
  }),
};

ProductCard.defaultProps = {
  asLink: false,
  imagePrefix: 'products/',
};

export default ProductCard;
